/**
 * MissingProjectsView — Story SCH-5.6.
 *
 * Lists SCH-imported projects flagged `external_status='missing_in_sch'` and
 * offers two admin decisions per row:
 *   - Keep as historical → external_status='kept_after_missing' (row stays
 *     in Joules; hides from this list).
 *   - Delete in Joules   → typed-confirm dialog, then hard DELETE.
 *
 * Re-link is automatic on the backend: if SCH starts returning the project,
 * the upserter clears external_status to NULL and the row drops out of this
 * list on the next poll-driven refetch.
 */

import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Loader2, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import {
  useDecideMissingProject,
  useMissingProjects,
} from '@/hooks/integrations/useMissingProjects';
import type { MissingProject } from '@/types/integrations/missingProjects';

export function MissingProjectsView() {
  const { toast } = useToast();
  const query = useMissingProjects();
  const decideMutation = useDecideMissingProject();
  const [deleteTarget, setDeleteTarget] = useState<MissingProject | null>(null);
  const [confirmText, setConfirmText] = useState('');

  const rows = query.data?.data?.data ?? [];

  function projectLabel(p: MissingProject): string {
    const customer = [p.firstName, p.lastName].filter(Boolean).join(' ').trim();
    if (p.projectName && customer) return `${p.projectName} — ${customer}`;
    return p.projectName || customer || `(unnamed project ${p.id.slice(0, 8)})`;
  }

  function handleKeep(p: MissingProject) {
    decideMutation.mutate(
      { projectId: p.id, decision: 'keep_historical' },
      {
        onSuccess: (result) => {
          if (result.error) {
            toast({
              title: 'Could not save decision',
              description: result.error.message,
              variant: 'destructive',
            });
          } else {
            toast({ title: `Kept as historical: ${projectLabel(p)}` });
          }
        },
        onError: () =>
          toast({
            title: 'Could not save decision',
            description: 'Please try again.',
            variant: 'destructive',
          }),
      },
    );
  }

  function handleDeleteConfirm() {
    if (!deleteTarget) return;
    const expected = projectLabel(deleteTarget);
    if (confirmText.trim() !== expected) return;

    decideMutation.mutate(
      { projectId: deleteTarget.id, decision: 'delete_joules' },
      {
        onSuccess: (result) => {
          if (result.error) {
            toast({
              title: 'Could not delete project',
              description: result.error.message,
              variant: 'destructive',
            });
          } else {
            toast({ title: `Deleted: ${projectLabel(deleteTarget)}` });
            setDeleteTarget(null);
            setConfirmText('');
          }
        },
        onError: () =>
          toast({
            title: 'Could not delete project',
            description: 'Please try again.',
            variant: 'destructive',
          }),
      },
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Missing in SubContractorHub</CardTitle>
        <p className="text-xs text-muted-foreground mt-0.5">
          Projects imported from SubContractorHub whose external ID is no longer
          returned by SCH. Joules never auto-deletes these — decide whether to
          keep each as historical data or delete it from Joules.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {query.isLoading && (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        )}

        {query.isError && (
          <Alert variant="destructive">
            <AlertTitle>Could not load missing projects</AlertTitle>
            <AlertDescription>
              {(query.error as Error)?.message ?? 'Please refresh the panel.'}
            </AlertDescription>
          </Alert>
        )}

        {!query.isLoading && !query.isError && rows.length === 0 && (
          <div className="py-8 text-center text-sm text-muted-foreground">
            No missing projects. SubContractorHub is returning every SCH-imported
            project this tenant currently has in Joules.
          </div>
        )}

        {rows.length > 0 && (
          <div className="overflow-x-auto border rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left">
                  <th className="px-3 py-2 font-medium">Project</th>
                  <th className="px-3 py-2 font-medium">External ID</th>
                  <th className="px-3 py-2 font-medium">Missing since</th>
                  <th className="px-3 py-2 font-medium" />
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((p) => {
                  const isPending =
                    decideMutation.isPending &&
                    decideMutation.variables?.projectId === p.id;
                  return (
                    <tr key={p.id}>
                      <td className="px-3 py-2">
                        <span className="font-medium">{projectLabel(p)}</span>
                      </td>
                      <td
                        className="px-3 py-2 text-muted-foreground font-mono text-xs truncate max-w-[180px]"
                        title={p.externalId ?? ''}
                      >
                        {p.externalId ?? '—'}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {p.missingSinceAt ? (
                          <span title={p.missingSinceAt}>
                            {formatDistanceToNow(new Date(p.missingSinceAt), {
                              addSuffix: true,
                            })}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            disabled={isPending}
                            onClick={() => handleKeep(p)}
                          >
                            {isPending &&
                              decideMutation.variables?.decision === 'keep_historical' && (
                                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                              )}
                            Keep as historical
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs text-destructive hover:text-destructive"
                            disabled={isPending}
                            onClick={() => {
                              setDeleteTarget(p);
                              setConfirmText('');
                            }}
                          >
                            <Trash2 className="mr-1 h-3 w-3" />
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Delete typed-confirm */}
        <AlertDialog
          open={deleteTarget !== null}
          onOpenChange={(open) => {
            if (!open) {
              setDeleteTarget(null);
              setConfirmText('');
            }
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete project from Joules?</AlertDialogTitle>
              <AlertDialogDescription>
                This permanently deletes the Joules project record. SubContractorHub
                history (in SCH itself) is not affected. To confirm, type the
                project label exactly:
                <br />
                <span className="mt-2 inline-block rounded bg-muted px-2 py-1 font-mono text-xs">
                  {deleteTarget ? projectLabel(deleteTarget) : ''}
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <Input
              autoFocus
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type the project label to confirm"
            />
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                disabled={
                  !deleteTarget ||
                  confirmText.trim() !== projectLabel(deleteTarget) ||
                  decideMutation.isPending
                }
                onClick={handleDeleteConfirm}
                className="bg-destructive hover:bg-destructive/90"
              >
                {decideMutation.isPending &&
                  decideMutation.variables?.decision === 'delete_joules' && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                Delete project
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
